import * as React from "react";
import { ChevronDown } from "lucide-react";

const Select = React.forwardRef(({ children, value, onValueChange, ...props }, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState(value);

  React.useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleValueChange = (newValue) => {
    setSelectedValue(newValue);
    onValueChange?.(newValue);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={ref} {...props}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            isOpen,
            selectedValue,
            onToggle: () => setIsOpen(!isOpen),
            onValueChange: handleValueChange,
            onClose: () => setIsOpen(false)
          });
        }
        return child;
      })}
    </div>
  );
});

const SelectTrigger = React.forwardRef(({ className, children, isOpen, selectedValue, onToggle, ...props }, ref) => {
  return (
    <button
      type="button"
      className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      onClick={onToggle}
      ref={ref}
      {...props}
    >
      {children}
      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
});

const SelectValue = ({ placeholder, selectedValue, children }) => {
  const getDisplayValue = () => {
    if (selectedValue) {
      // Try to find the matching SelectItem to get its display text
      const content = React.Children.toArray(children).find(child => 
        React.isValidElement(child) && child.props.value === selectedValue
      );
      return content?.props?.children || selectedValue;
    }
    return placeholder;
  };

  return (
    <span className={selectedValue ? "text-gray-900" : "text-gray-500"}>
      {getDisplayValue()}
    </span>
  );
};

const SelectContent = React.forwardRef(({ className, children, isOpen, onValueChange, onClose, ...props }, ref) => {
  const contentRef = React.useRef();

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={contentRef}
      className={`absolute top-full left-0 z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg ${className}`}
      {...props}
    >
      <div className="max-h-60 overflow-y-auto p-1">
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              onSelect: onValueChange
            });
          }
          return child;
        })}
      </div>
    </div>
  );
});

const SelectItem = React.forwardRef(({ className, children, value, onSelect, ...props }, ref) => {
  return (
    <button
      type="button"
      className={`w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${className}`}
      onClick={() => onSelect?.(value)}
      ref={ref}
      {...props}
    >
      {children}
    </button>
  );
});

SelectTrigger.displayName = "SelectTrigger";
SelectValue.displayName = "SelectValue";
SelectContent.displayName = "SelectContent";
SelectItem.displayName = "SelectItem";

export {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
};